import React, { useState } from "react";
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useEaData } from "@/components/energy-analytics/analytics/useEaData";
import { EaChartCard, EaKpiCard, EaNote, EaPageHeader, EaSeg, inputCls } from "@/components/energy-analytics/analytics/EaUi";
import { COLORS, MONTHS_IT, SCOPES, SCOPE_LABEL, fmtEur, fmtQty, fmtSignedPct, monthsSpan } from "@/components/energy-analytics/analytics/eaFormat";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EffSites } from "./EaEffSites";
import { EffValues } from "./EaEffValues";

const axis = { tick: { fontSize: 11, fill: "#64748B" }, axisLine: false, tickLine: false };
const dp = (a, b) => (a != null && b ? (a / b - 1) * 100 : null);

function SiteKpis({ s, dr }) {
  const k = dr.kpis, y = dr.yoy, u = dr.unit;
  const yo = (key) => (y ? `${fmtSignedPct(dp(y.current[key], y.previous[key]))} vs anno prec. (${monthsSpan(y.months)})` : "Anno precedente: driver non compilati");
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
      <EaKpiCard accent={COLORS.power} label={`kWh per ${u}`} value={fmtQty(k.kwh_per_unit, 1, `kWh/${u}`)} sub={yo("kwh_per_unit")} testid="eff-kpi-kwh" />
      <EaKpiCard accent={COLORS.gas} label={`Smc per ${u}`} value={fmtQty(k.smc_per_unit, 2, `Smc/${u}`)} sub={yo("smc_per_unit")} testid="eff-kpi-smc" />
      <EaKpiCard accent={COLORS.total} label={`Costo energia per ${u}`} value={fmtQty(k.eur_per_unit, 2, `€/${u}`)} sub={`${yo("eur_per_unit")} · solo mesi fatturati`} testid="eff-kpi-eur" />
      <EaKpiCard accent={COLORS.total} label={`TEP per ${u}`} value={fmtQty(k.tep_per_unit, 4, `tep/${u}`)} sub={`Totale ${fmtQty(k.tep_total, 1, "tep")} · ${yo("tep_per_unit")}`} testid="eff-kpi-tep" />
      <EaKpiCard accent="#16A34A" label={`CO₂ per ${u}`} value={fmtQty(k.co2_kg_per_unit, 1, `kg/${u}`)} sub={`Totale ${fmtQty(k.co2_t_total, 1, "t CO₂")} · ${yo("co2_kg_per_unit")}`} testid="eff-kpi-co2" />
      <EaKpiCard accent={COLORS.total} label={`${dr.name} (${u})`} value={fmtQty(k.driver_total, 0, u)} sub={`${k.months.length} mesi con driver e consumi · ${s.scope === "point" ? "singolo punto" : `${s.points} ${s.points === 1 ? "punto" : "punti"} ${s.scope === "company" ? "in azienda" : "nel sito"}`}`} testid="eff-kpi-driver" />
    </div>
  );
}

function Trend({ dr }) {
  const [m, setM] = useState("kwh");
  const key = m === "kwh" ? "kwh_per_unit" : "smc_per_unit";
  const data = dr.monthly.map((r) => ({ label: MONTHS_IT[r.month - 1], cur: r[key], prev: r[`prev_${key}`], driver: r.driver }));
  return (
    <EaChartCard title={`Andamento mensile ${m === "kwh" ? "kWh" : "Smc"} per ${dr.unit}`} subtitle="Barre = anno selezionato, linea = anno precedente (stesso mese)" testid="eff-trend"
      right={<EaSeg value={m} onChange={setM} options={[["kwh", "Power"], ["smc", "Gas"]]} testid="eff-trend-metric" />}>
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
            <XAxis dataKey="label" {...axis} />
            <YAxis {...axis} width={60} tickFormatter={(v) => fmtQty(v, 0)} />
            <Tooltip formatter={(v, n) => [fmtQty(v, 2), n]} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="cur" name="Anno selezionato" fill={m === "kwh" ? COLORS.power : COLORS.gas} radius={[3, 3, 0, 0]} />
            <Line dataKey="prev" name="Anno precedente" stroke="#475569" strokeDasharray="5 3" strokeWidth={2} dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </EaChartCard>
  );
}

function Compare({ sites }) {
  const rows = sites.flatMap((s) => s.drivers.map((d) => ({ s, d })));
  return (
    <div className="card-elevated overflow-x-auto" data-testid="eff-compare">
      <div className="p-4 font-display font-semibold text-slate-900" data-pdf-title>Confronto tra ambiti</div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
          <tr>{["Ambito", "Driver", "kWh/unità", "Smc/unità", "€/unità", "TEP/unità", "kg CO₂/unità", "Δ kWh/unità vs anno prec."].map((h, i) => <th key={h} className={`px-3 py-2 ${i > 1 ? "text-right" : "text-left"}`}>{h}</th>)}</tr>
        </thead>
        <tbody className="font-mono-nums">
          {rows.map(({ s, d }) => (
            <tr key={`${s.id}-${d.id}`} className="border-t border-slate-100" data-testid={`eff-compare-${s.id}-${d.id}`}>
              <td className="px-3 py-2 font-sans font-semibold">{s.name}<span className="block text-[11px] font-normal text-slate-400">{SCOPE_LABEL[s.scope]}</span></td><td className="px-3 py-2 font-sans">{d.name} ({d.unit})</td>
              <td className="px-3 py-2 text-right">{fmtQty(d.kpis.kwh_per_unit, 1)}</td><td className="px-3 py-2 text-right">{fmtQty(d.kpis.smc_per_unit, 2)}</td>
              <td className="px-3 py-2 text-right">{fmtQty(d.kpis.eur_per_unit, 2)}</td><td className="px-3 py-2 text-right">{fmtQty(d.kpis.tep_per_unit, 4)}</td>
              <td className="px-3 py-2 text-right">{fmtQty(d.kpis.co2_kg_per_unit, 1)}</td>
              <td className="px-3 py-2 text-right">{d.yoy ? fmtSignedPct(dp(d.yoy.current.kwh_per_unit, d.yoy.previous.kwh_per_unit)) : "n.c."}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function KpiTab({ d }) {
  const withDrv = d.sites.filter((s) => s.drivers.length);
  const avail = SCOPES.filter(([k]) => withDrv.some((s) => s.scope === k));
  const [sc, setSc] = useState(avail[0]?.[0] || "site");
  const list = withDrv.filter((x) => x.scope === sc);
  const [sid, setSid] = useState("");
  const s = list.find((x) => x.id === sid) || list[0];
  const [did, setDid] = useState("");
  const dr = s?.drivers.find((x) => x.id === did) || s?.drivers[0];
  if (!s) return <EaNote testid="eff-kpi-empty">Nessun ambito con driver: nella scheda "Ambiti e driver" configura il totale aziendale, un sito o un singolo POD/PDR e inserisci i valori mensili.</EaNote>;
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2 items-center">
        <EaSeg value={sc} onChange={(v) => { setSc(v); setSid(""); setDid(""); }} options={avail} testid="eff-kpi-scope" />
        {sc !== "company" && <select value={s.id} onChange={(e) => { setSid(e.target.value); setDid(""); }} className={inputCls} data-testid="eff-kpi-site">{list.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select>}
        <select value={dr.id} onChange={(e) => setDid(e.target.value)} className={inputCls} data-testid="eff-kpi-driver-select">{s.drivers.map((x) => <option key={x.id} value={x.id}>{x.name} ({x.unit})</option>)}</select>
      </div>
      {!dr.values_count && <EaNote testid="eff-kpi-novalues">Non calcolabile: in database mancano i valori del driver "{dr.name}" per il {d.year}. Compilali nella scheda "Valori mensili".</EaNote>}
      <SiteKpis s={s} dr={dr} />
      <EaKpiCard label={sc === "company" ? "Spesa e consumi aziendali" : sc === "point" ? "Spesa e consumi del punto" : "Spesa e consumi del sito"} value={fmtEur(s.totals.spend)} testid="eff-site-totals"
        sub={`Power ${fmtQty(s.totals.kwh, 0, "kWh")} · Gas ${fmtQty(s.totals.smc, 0, "Smc")} · ${fmtQty(s.totals.tep, 1, "tep")} · ${fmtQty(s.totals.co2_t, 1, "t CO₂")}`} />
      <Trend dr={dr} />
      {withDrv.length > 0 && <Compare sites={withDrv} />}
      <EaNote>KPI = consumi a consuntivo (o spesa fatturata) dei punti dell'ambito ÷ valore del driver, solo nei mesi in cui entrambi sono presenti. Fattori {d.year}: {fmtQty(d.factors.tep_per_mwh, 3)} tep/MWh, {fmtQty(d.factors.tep_per_ksmc, 3)} tep/1.000 Smc, {fmtQty(d.factors.co2_kg_per_kwh, 4)} kg CO₂/kWh, {fmtQty(d.factors.co2_kg_per_smc, 3)} kg CO₂/Smc (fattori emissivi: {d.factors.co2_source}, modificabili in Sostenibilità).</EaNote>
    </div>
  );
}

export default function EaEfficiency() {
  const [v, setV] = useState(0);
  const { data: d, loading, error } = useEaData("/ea/efficiency/kpis", { v });
  const sites = useEaData("/ea/efficiency/sites", { v });
  const reload = () => setV((x) => x + 1);
  return (
    <div className="space-y-6" data-testid="ea-efficiency">
      <EaPageHeader title="KPI" subtitle={`KPI di efficienza: consumi, costi, TEP e CO₂ per unità di driver (produzione, ore, m², dipendenti…) per totale aziendale, sito o singolo POD/PDR${d?.year ? ` · anno ${d.year}` : ""}.`} />
      {loading && !d ? <div className="text-sm text-slate-400" data-testid="ea-loading">Caricamento…</div> : error ? <EaNote>{error}</EaNote> : d && (
        <Tabs defaultValue="kpi">
          <TabsList data-testid="eff-tabs"><TabsTrigger value="kpi" data-testid="eff-tab-kpi">KPI</TabsTrigger><TabsTrigger value="sites" data-testid="eff-tab-sites">Ambiti e driver</TabsTrigger><TabsTrigger value="values" data-testid="eff-tab-values">Valori mensili</TabsTrigger></TabsList>
          <TabsContent value="kpi" className="mt-4"><KpiTab key={v} d={d} /></TabsContent>
          <TabsContent value="sites" className="mt-4">{sites.data && <EffSites data={sites.data} reload={reload} />}</TabsContent>
          <TabsContent value="values" className="mt-4">{sites.data && <EffValues data={sites.data} year={d.year} reload={reload} />}</TabsContent>
        </Tabs>
      )}
    </div>
  );
}
